<template>
  <div>
    <v-alert
      color="primary"
      :title="$t('connection-manager.title')"
      :text="$t('connection-manager.text')"
      class="mb-3"
    />
    <v-card>
      <Feature :feature-slug="featureNames.CRM">
        <ConnectionSegment
          :title="$t('connection-manager.connections.crm.title')"
          :description="$t('connection-manager.connections.crm.description')"
        >
          <CrmWrapper
            v-for="connection in crmConnections"
            :key="connection.id"
            :provider="connection.provider"
          >
            <template
              #activator="{ props, isConnected, loading, connect, settings, disabled, disconnect }"
            >
              <Connection
                :is-new="connection.isNew"
                :active="isConnected"
                :disabled="disabled || needsUpgrade"
                :image="connection.image"
                :loading="loading"
                :dialogActivatorProps="props"
                :connectionButtonText="`connection-manager.crm.${connection.provider.toLowerCase()}.connect`"
                show-settings
                @connect="connect"
                @settings="settings"
                @disconnect="disconnect"
              />
            </template>
          </CrmWrapper>
        </ConnectionSegment>
      </Feature>
      <BlurredBackdropUpselling v-if="needsUpgrade" />
      <ConnectionSegment
        :title="$t('connection-manager.connections.social-media.title')"
        :description="$t('connection-manager.connections.social-media.description')"
      >
        <SocialMediaWrapper
          v-for="connection in socialConnections"
          :key="connection.id"
          :provider="connection.provider"
          :connected-media="socialMediaConnections"
        >
          <template
            #activator="{ props, isConnected, loggedAs, loading, disabled, connect, disconnect }"
          >
            <Connection
              :is-new="connection.isNew"
              :active="isConnected"
              :disabled="disabled"
              :image="connection.image"
              :loading="loading"
              :dialogActivatorProps="props"
              :tooltip-content="disabled ? connection.tooltip : null"
              :needs-upgrade="connection.needsUpgrade"
              :text-below-image="connection.textBelowImage"
              verified-text="connection-manager.connected"
              @connect="connect"
              @disconnect="disconnectSocialMediaWrapper(disconnect)"
            >
              <template
                #default
                v-if="loggedAs"
              >
                <div class="social-media-logged-as">
                  {{ loggedAs }}
                </div>
              </template>
            </Connection>
          </template>
        </SocialMediaWrapper>
      </ConnectionSegment>
      <!-- GOOGLE_CALENDAR: Connection Manager only (see featureNames.js). -->
      <Feature :feature-slug="featureNames.GOOGLE_CALENDAR">
        <GoogleCalendarConnectionSegment />
        <template #inactive>
          <GoogleCalendarConnectionSegment :feature-disabled="true" />
        </template>
        <template #disabled>
          <GoogleCalendarConnectionSegment :feature-disabled="true" />
        </template>
      </Feature>
      <Feature :feature-slug="featureNames.KONSTANTINE">
        <ConnectionSegment :title="$t('connection-manager.connections.ai.title')">
          <Konstantine />
        </ConnectionSegment>
      </Feature>
      <Feature :feature-slug="featureNames.CONNECTION_MANAGER_IMPORT_EXPORT">
        <ConnectionSegment
          :title="$t('connection-manager.connections.export.title')"
          :description="$t('connection-manager.connections.export.description')"
        >
          <ImportExportWrapper
            v-for="connection in importExportConnections"
            :key="connection.id"
            :provider="connection.provider"
          >
            <template #activator="{ importAction, exportAction, isExportProcessing }">
              <Connection
                :image="connection.image"
                :active="false"
              >
                <template #image="{ image }">
                  <r-img
                    :src="image"
                    width="80"
                    height="48"
                  />
                  <p class="mb-0 font-weight-medium">{{ connection.title }}</p>
                </template>
                <template #actions>
                  <div class="d-flex align-center">
                    <v-btn
                      variant="text"
                      @click="importAction"
                    >
                      <v-icon class="">mdi-upload</v-icon>
                      {{ $t('connection-manager.import-export.import') }}
                    </v-btn>
                    <v-icon icon="mdi-slash-forward" />
                    <v-btn
                      variant="text"
                      :loading="isExportProcessing"
                      @click="exportAction"
                    >
                      <v-icon class="icon--rotate">mdi-upload</v-icon>
                      {{ $t('connection-manager.import-export.export') }}
                    </v-btn>
                  </div>
                </template>
              </Connection>
            </template>
          </ImportExportWrapper>
        </ConnectionSegment>
      </Feature>
      <ConnectionSegment
        :title="$t('connection-manager.connections.social-proof.title')"
        :description="$t('connection-manager.connections.social-proof.description')"
      >
        <Feature :feature-slug="featureNames.GOOGLE_REVIEW">
          <GoogleReviewWrapper>
            <template #activator="{ props, status, loading }">
              <Connection
                image="/img/google-review.svg"
                :loading="loading"
                :active="false"
                :dialog-activator-props="props"
                :connection-button-text="$t('navigation.labels.settings')"
              >
                <ProvenExpertStatusChip
                  namespace="google-reviews"
                  :status="status"
                />
              </Connection>
            </template>
          </GoogleReviewWrapper>
        </Feature>
        <Feature :feature-slug="featureNames.PROVEN_EXPERT">
          <ProvenExpertWrapper>
            <template #activator="{ props, status, loading }">
              <Connection
                image="/img/proven-expert.png"
                :loading="loading"
                :active="false"
                :dialog-activator-props="props"
                :connection-button-text="$t('connection-manager.proven-expert.setup')"
              >
                <ProvenExpertStatusChip :status="status" />
              </Connection>
            </template>
          </ProvenExpertWrapper>
        </Feature>
      </ConnectionSegment>
    </v-card>
    <ConfirmationDialog
      ref="confirmationDialog"
      :title="$t('connection-manager.confirmation-dialog.title')"
      :text="$t('connection-manager.confirmation-dialog.social-text')"
      :ok-button-text="$t('connection-manager.confirmation-dialog.confirm-button-text')"
      color="error"
      ok-button-variant="text"
    />
  </div>
</template>
<script lang="ts" setup>
import { useApolloClient } from '@vue/apollo-composable'
import { computed, inject, nextTick, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { AuthInstance } from '@/auth'
import BlurredBackdropUpselling from '@/components/BlurredBackdropUpselling.vue'
import ConfirmationDialog from '@/components/ConfirmationDialog.vue'
import { useBranding } from '@/composables/useBranding'
import { useCrm } from '@/composables/useCrm'
import { useSocialMedia } from '@/composables/useSocialMedia'
import { CrmProvider, SocialMediaType } from '@/generated/graphql'
import eventBus, { eventNames } from '@/lib/eventBus'
import featureNames from '@/lib/featureNames'
import { isLightUser } from '@/lib/productTypes'
import Konstantine from '@/modules/dashboard/konstantine/Konstantine.vue'
import CONNECT_TO_EMPRO from '@/modules/settings/crmSettings/empro/queries/ConnectToEmpro.gql'
import { Feature } from '@/plugins/feature-config'

import Connection from './Connection.vue'
import ConnectionSegment from './ConnectionSegment.vue'
import CrmWrapper from './crmWrapper/index.vue'
import GoogleCalendarConnectionSegment from './GoogleCalendarConnectionSegment.vue'
import GoogleReviewWrapper from './googleReviewWrapper/index.vue'
import ImportExportWrapper from './importExportWrapper/index.vue'
import ProvenExpertStatusChip from './ProvenExpertStatusChip.vue'
import ProvenExpertWrapper from './provenExpertWrapper/index.vue'
import SocialMediaWrapper from './socialMediaWrapper/index.vue'

type ConnectionOption = {
  id: string
  image: string
  isNew?: boolean
  disabled?: boolean
  tooltip?: string
  textBelowImage?: string
  needsUpgrade?: boolean
}
const {
  socialMediaConnections,
  loadSocialMediaConnections,
  refetchSocialMediaConnections,
  contentCreatorFeature,
} = useSocialMedia()
const { providers } = useCrm()
const route = useRoute()
const router = useRouter()
const { client } = useApolloClient()

const { t } = useI18n()
const $auth = inject<AuthInstance>('auth')

const { isPlatformSmg, isPlatformTurkoglu } = useBranding()
const needsUpgrade = computed(
  () => (isPlatformSmg?.value || isPlatformTurkoglu?.value) && isLightUser($auth?.user)
)

const confirmationDialog = ref<InstanceType<typeof ConfirmationDialog> | null>(null)

const crmConnections = computed(() => {
  if (!providers.value.length) return []

  const imageMapper = {
    [CrmProvider.Flowfact]: '/img/flowfact.png',
    [CrmProvider.Propstack]: '/img/propstack.png',
    [CrmProvider.Onoffice]: '/img/onOffice-new.png',
    [CrmProvider.Casaone]: '/img/casaone.svg',
    [CrmProvider.Empro]: '/img/empro.png',
  }
  return providers.value.map((provider) => ({
    id: provider.value,
    image: imageMapper[provider.value],
    provider: provider.value,
    isNew: false,
  }))
})

const socialConnections = computed<(ConnectionOption & { provider: SocialMediaType })[]>(() => [
  {
    id: 'facebook',
    image: '/img/facebook.png',
    provider: SocialMediaType.Facebook,
  },
  {
    id: 'instagram',
    image: '/img/instagram.png',
    provider: SocialMediaType.Instagram,
    tooltip: t('social-media.connect-instagram.info'),
    needsUpgrade: needsUpgrade?.value,
  },
  {
    id: 'linkedin',
    image: '/img/linkedin.png',
    provider: SocialMediaType.Linkedin,
    needsUpgrade: linkedinNeedsUpgrade.value,
  },
  ...(contentCreatorFeature.value?.config?.postToGoogleBusiness
    ? [
        {
          id: 'google-business',
          image: '/img/kindTypes/google-business.svg',
          textBelowImage: t('social-media.socials.google-business-profile'),
          provider: SocialMediaType.GoogleBusiness,
          isNew: true,
        },
      ]
    : []),
])

const importExportConnections = computed<
  (ConnectionOption & { title: string; provider: string })[]
>(() => [
  {
    id: 'excel',
    image: '/img/excel.svg',
    provider: 'excel',
    title: t('connection-manager.import-export.excel'),
  },
  {
    id: 'google-sheets',
    image: '/img/google-sheets.svg',
    provider: 'google-sheets',
    title: t('connection-manager.import-export.google-sheets'),
  },
  {
    id: 'apple-numbers',
    image: '/img/apple-numbers.svg',
    provider: 'apple-numbers',
    title: t('connection-manager.import-export.apple-numbers'),
  },
])

const linkedinNeedsUpgrade = computed(() => isLightUser($auth?.user))

const disconnectSocialMediaWrapper = async (cb: () => Promise<void>) => {
  if (!(await confirmationDialog.value?.confirm())) return
  await cb()
  await nextTick()
  await refetchSocialMediaConnections()
}

const handlingOAuth = ref(false)
const handleEmproOAuthCallback = async () => {
  if (handlingOAuth.value) return

  const code = typeof route.query.code === 'string' ? route.query.code : null
  const state = typeof route.query.state === 'string' ? route.query.state : null
  if (!code) return

  const storedState = localStorage.getItem('oauth_state')
  // Google Calendar (and other) OAuth returns code/state without Empro's localStorage marker — leave query intact.
  if (!storedState) return

  handlingOAuth.value = true

  try {
    if (!state || state !== storedState) {
      localStorage.removeItem('oauth_state')
      return
    }

    localStorage.removeItem('oauth_state')

    await router.replace({ name: 'connection-manager', query: {} }).catch(() => {})
    await client.mutate({
      mutation: CONNECT_TO_EMPRO,
      variables: { input: { code } },
    })

    await nextTick()
    eventBus.$emit(eventNames.OPEN_CRM_WIZARD, { provider: 'EMPRO' })
  } catch (e) {
    console.error('connectToEmpro failed', e)
  } finally {
    handlingOAuth.value = false
  }
}

watch(
  () => [route.query.code, route.query.state],
  async () => {
    await handleEmproOAuthCallback()
  },
  { immediate: true }
)
onMounted(async () => {
  await loadSocialMediaConnections()
})
</script>
<style scoped>
.social-media-logged-as {
  padding: 10px 0;
}
.icon--rotate {
  transform: rotate(180deg);
}
</style>
